import type { AppProps } from 'next/app';

import { useCookieConsent } from '@zapier/cookies';
import { IdentityProvider, SessionProvider } from '@zapier/identity';
import { PreviousUrlProvider } from '@zapier/auth-pages';
import { GlobalStylesProvider } from '@zapier/design-system';
import {
  FullStory,
  nextJsReportWebVitals,
  setFaroTrackingConsent,
  TrackingProvider,
  TrackRouting,
} from '../observability';
import { setTag } from '@sentry/nextjs';
import {
  FeatureFlagProvider,
  isBrowser,
  isInIframe,
} from '@zapier/identity-utils';
import { Fragment, useEffect, useState } from 'react';
import { IntlError, IntlErrorCode, NextIntlClientProvider } from 'next-intl';
import type { TreatmentsWithConfig } from '@splitsoftware/splitio/types/splitio';
import messages from '../../messages/en.json';
import './global.css';
// TODO: These lint errors are happening because ESLint isn't using the modern
// node module resolution algorithm.
//
// https://zapierorg.atlassian.net/browse/EE-1258
//
// eslint-disable-next-line import/no-unresolved
import '@zapier/design-tokens/custom-properties.css';
// eslint-disable-next-line import/no-unresolved
import '@zapier/design-system/style.css';
// eslint-disable-next-line import/no-unresolved
import '@zapier/marketing-ui/styles.css';
// eslint-disable-next-line import/no-unresolved
import '@zapier/zinnia-icons/style.css';

// Measure and report web vitals that Next.js tracks to Axiom.
// See: https://nextjs.org/docs/advanced-features/measuring-performance
export const reportWebVitals = nextJsReportWebVitals;

type AppPropsWithTreatments = AppProps & {
  pageProps: {
    treatments?: TreatmentsWithConfig;
    serverRender?: boolean;
  };
};

function getMessageFallback({
  namespace,
  key,
  error,
}: {
  namespace?: string;
  key: string;
  error: IntlError;
}): string {
  const path = [namespace, key].filter(part => part != null).join('.');

  if (error.code === IntlErrorCode.MISSING_MESSAGE) {
    console.error('MISSING_MESSAGE', path);
    return path + ' is not yet translated';
  } else {
    console.error('OTHER_MESSAGE', path);
    return 'Dear developer, please fix this message: ' + path;
  }
}

function App({ Component, pageProps }: AppPropsWithTreatments) {
  const [isClient, setIsClient] = useState(false);
  const cookieConsent = useCookieConsent();

  // Set during render, not in a useEffect, so the Sentry tag is already on the
  // scope when a hydration error is reported. Browser only, because hydration
  // errors happen in the browser, and the browser has one visitor per page, so
  // the Sentry tag can't attach to another request's events.
  if (isBrowser()) {
    setTag('server_render', String(Boolean(pageProps.serverRender)));
  }

  useEffect(() => {
    setIsClient(typeof window !== 'undefined');
  }, []);

  // Faro initializes before first paint in instrumentation-client.ts; here we only
  // keep its tracking consent in sync as OneTrust analytics consent resolves or changes.
  useEffect(() => {
    setFaroTrackingConsent(cookieConsent.byName.analytics);
  }, [cookieConsent.byName.analytics]);

  // Pages render in the browser only, after their JavaScript has loaded,
  // unless getServerSideProps returns `serverRender: true`. Components across
  // the app read browser-only values such as `window` while rendering, so a
  // page opts in once its render path also works on the server. A page that
  // opts in renders the same markup on the server and on the browser's first
  // render, so hydration matches.
  if (!isClient && !pageProps.serverRender) {
    return <></>;
  }

  return (
    <NextIntlClientProvider
      getMessageFallback={getMessageFallback}
      locale="en"
      messages={messages}
    >
      <IdentityProvider>
        {/* Every page here is the login/signup surface itself, so an anonymous visitor is expected, not a reason to redirect to login. */}
        <SessionProvider redirectOnUnauthenticated={false}>
          <FeatureFlagProvider treatments={pageProps.treatments ?? {}}>
            <PreviousUrlProvider>
              <TrackingProvider>
                <TrackRouting>
                  <GlobalStylesProvider isRebrand={true} isPlatform={true}>
                    <Component {...pageProps} />
                  </GlobalStylesProvider>
                </TrackRouting>
              </TrackingProvider>
            </PreviousUrlProvider>
          </FeatureFlagProvider>
        </SessionProvider>
      </IdentityProvider>
      {/* isInIframe() always returns false on the server. Waiting for
          isClient renders FullStory only once isInIframe() can check the
          browser's window. */}
      {isClient && !isInIframe() && <FullStory />}
    </NextIntlClientProvider>
  );
}

export default App;
